import { z } from 'zod';
import { nullableText } from '../sanitised-text.js';
import { CalendarDateOnlySchema, PaginationQuerySchema, createPaginatedResponseSchema } from './common.js';
/**
 * The broad kind of a document an account keeps on its own. Tags carry what it
 * is held for and any finer kind. A string column, not a database enum, so a
 * new value needs no migration.
 */
export const AccountDocumentTypeSchema = z.enum([
    'BANK_STATEMENT',
    'ACCOUNT_STATEMENT',
    'INVOICE',
    'TAX',
    'FINANCIAL_STATEMENTS',
    'LEGAL',
    'IDENTITY',
    'OTHER',
]);
const DESCRIPTION_MAX_LENGTH = 1000;
const description = nullableText(DESCRIPTION_MAX_LENGTH, `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer`);
/**
 * One tag in the form it is stored: trimmed, lowercased, whitespace runs turned
 * into hyphens, so `' Bank Confirmation '` and `'bank-confirmation'` are the
 * same tag. Canonicalises only; the document service owns validation.
 */
export function canonicaliseDocumentTag(raw) {
    return raw.trim().toLowerCase().replace(/\s+/g, '-');
}
/** A tag list in the form it is stored: each tag canonicalised, deduplicated and sorted. */
export function canonicaliseDocumentTags(raw) {
    return [...new Set(raw.map(canonicaliseDocumentTag))].sort();
}
// Tags are accepted as given; the document service canonicalises them and owns
// the character, length and count rules, so one set of messages applies to
// upload, update and the list filter alike.
const tags = z.array(z.string());
/** The `metadata` form field of an account document upload. */
export const UploadAccountDocumentMetadataSchema = z.strictObject({
    idempotencyKey: z.string().min(1).max(255, 'Idempotency key must be 255 characters or less'),
    documentType: AccountDocumentTypeSchema,
    tags: tags.optional(),
    description,
    documentDate: CalendarDateOnlySchema.nullable().optional(),
});
/** An omitted field is unchanged, `null` clears it, and `tags` replaces the list. */
export const UpdateAccountDocumentInputSchema = z
    .strictObject({
    documentType: AccountDocumentTypeSchema.optional(),
    tags: tags.optional(),
    description,
    documentDate: CalendarDateOnlySchema.nullable().optional(),
})
    .refine((data) => Object.keys(data).length > 0, { message: 'At least one field must be supplied' });
/** Filters combine with AND; `tags` is comma-separated and a document must carry all of them. */
export const ListAccountDocumentsQuerySchema = z.strictObject({
    ...PaginationQuerySchema.shape,
    documentType: AccountDocumentTypeSchema.optional(),
    tags: z
        .string()
        .transform((value) => value.split(',').map((tag) => tag.trim()).filter(Boolean))
        .optional(),
    dateFrom: CalendarDateOnlySchema.optional(),
    dateTo: CalendarDateOnlySchema.optional(),
});
export const AccountDocumentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int(),
    documentType: AccountDocumentTypeSchema,
    tags: z.array(z.string()),
    description: z.string().nullable(),
    documentDate: CalendarDateOnlySchema.nullable(),
    archivedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    downloadUrl: z.string(),
});
export const AccountDocumentListResponseSchema = createPaginatedResponseSchema(AccountDocumentSchema);
export const AccountDocumentTagsResponseSchema = z.object({
    tags: z.array(z.object({ tag: z.string(), documentCount: z.number().int() })),
});
